import { useState, useEffect, useRef } from 'react';

export default function BrandStatement() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const link = document.createElement('link');
    link.href =
      'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400;1,600&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.2 }
    );

    const el = sectionRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
      if (link.parentNode) link.parentNode.removeChild(link);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="cam-ket"
      className="relative overflow-hidden py-16 md:py-20 bg-[#2a4b7c]"
    >
      {/* Dot pattern – rất nhẹ như ảnh mẫu */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          opacity: 0.6,
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-8 lg:px-16 text-center">
        {/* Quote – lớn, teal */}
        <div
          className={`transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
          }`}
        >
          <i
            className="ri-double-quotes-l text-5xl md:text-6xl lg:text-7xl text-[#4ED4F4] block mb-2"
            aria-hidden
          />
        </div>

        {/* Nội dung – wrapper inline-block để các dòng ngắn căn giữa dưới dòng trên */}
        <div className="inline-block text-center">
          <h2
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal italic leading-[1.75] text-center transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              letterSpacing: '0.03em',
            }}
          >
            <span className="block text-white md:whitespace-nowrap mb-0.5">Chúng tôi không bán phần mềm.</span>
            <span className="block text-white mb-0.5">Chúng tôi kiến tạo</span>
            <span className="relative inline-block">
              <span className="block text-[#4ED4F4] mb-0.5">nền tảng số bền vững, độc</span>
              <span className="block text-[#4ED4F4] mb-0.5">lập và an toàn</span>
              <span
                className={`absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#4ED4F4] to-transparent transition-all duration-1000 delay-800 ${
                  isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
                }`}
                style={{ transformOrigin: 'left' }}
              />
            </span>
            <span className="block text-white mt-0.5">cho doanh nghiệp của bạn.</span>
          </h2>
        </div>

        {/* Gạch ngang trang trí – ngắn, cyan nhạt */}
        <div
          className={`w-24 h-0.5 bg-gradient-to-r from-transparent via-[#4ED4F4]/80 to-transparent mx-auto mt-10 transition-all duration-1000 delay-1000 ${
            isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
          }`}
          style={{ transformOrigin: 'center' }}
        />

        {/* Tagline – sans-serif, màu tối gần nền */}
        <p
          className={`text-[11px] font-semibold uppercase tracking-[0.35em] mt-6 text-[#8b9cb8] transition-all duration-1000 delay-[1.1s] ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
          }`}
        >
          Tư vấn độc lập tuyệt đối
        </p>
      </div>

    </section>
  );
}
